import { useState, useCallback } from "react";
import "leaflet";
import { toast } from "sonner";
import { CalcPageHeader } from "@/components/ResultsPanel";
import ResultsPanel from "@/components/ResultsPanel";
import EduSection from "@/components/EduSection";
import MapSearchBar from "@/components/map/MapSearchBar";
import MapCanvas from "@/components/map/MapCanvas";
import useAddressSearch from "@/hooks/useAddressSearch";
import usePolygonMetrics from "@/hooks/usePolygonMetrics";
import { unitSqm } from "@/lib/stateUnits";
import { formatNumber } from "@/lib/units";
import { useApp } from "@/lib/appContext";
import { stateName } from "@/lib/states";
import { useSeo } from "@/lib/seo";

const DEFAULT_CENTER = [26.8467, 80.9462]; // Lucknow

export default function MapArea() {
  const { state } = useApp();
  const [points, setPoints] = useState([]);
  const [tile, setTile] = useState("satellite");
  const [center, setCenter] = useState(DEFAULT_CENTER);

  const search = useAddressSearch();
  const { areaSqm, closed, rows } = usePolygonMetrics(points, state);

  useSeo({
    title: "Map Area Calculator — Draw & Measure Land",
    description: "Search any address in India and draw a polygon on the map to compute exact area in Bigha, Katha, Acre, Sq ft and more. Free satellite view.",
    path: "/map-area",
  });

  const addPoint = useCallback((p) => setPoints((prev) => [...prev, p]), []);
  const undo = useCallback(() => setPoints((p) => p.slice(0, -1)), []);
  const clear = useCallback(() => setPoints([]), []);
  const toggleTile = useCallback(() => setTile((t) => (t === "satellite" ? "street" : "satellite")), []);

  const pickSuggestion = useCallback((s) => {
    const lat = parseFloat(s.lat), lng = parseFloat(s.lon);
    setCenter([lat, lng]);
    search.setQuery(s.display_name);
    search.setSuggestions([]);
  }, [search]);

  const locate = useCallback(() => {
    if (!navigator.geolocation) { toast.error("Geolocation unavailable"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setCenter([pos.coords.latitude, pos.coords.longitude]),
      (err) => { console.error(err); toast.error("Unable to fetch your location"); }
    );
  }, []);

  const highlight = closed
    ? { label: "Area", value: `${formatNumber(areaSqm)} m²`, sub: `${formatNumber(areaSqm / unitSqm("bigha", state))} Bigha · ${formatNumber(areaSqm / unitSqm("acre", state))} Acre` }
    : { label: "Drawing", value: "—", sub: "Add at least 3 points" };

  return (
    <div>
      <CalcPageHeader
        title="Map Area Calculator"
        description={`Click points on the map to draw your plot boundary. Live geodesic area — regional units for ${stateName(state)}.`}
        slug="map-area"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-8 space-y-4">
          <MapSearchBar
            query={search.query}
            onQueryChange={search.onQueryChange}
            onClear={search.clear}
            suggestions={search.suggestions}
            loading={search.loading}
            onPick={pickSuggestion}
            tile={tile}
            onToggleTile={toggleTile}
            onLocate={locate}
          />
          <MapCanvas
            center={center}
            tile={tile}
            points={points}
            onAdd={addPoint}
            onUndo={undo}
            onClear={clear}
          />
        </div>

        <div className="lg:col-span-4 lg:sticky lg:top-24">
          <ResultsPanel
            title="Map Area"
            slug="map-area"
            highlight={highlight}
            rows={rows}
            onReset={clear}
            note={`Geodesic (WGS84) polygon area — accurate for plots up to a few km². Regional units use ${stateName(state)} factors.`}
          />
        </div>
      </div>

      <EduSection
        howItWorks={<>
          <p><strong>How to measure:</strong> search your address or tap &quot;My location&quot;, then click each corner of your plot on the map in order. LandCalc auto-closes the polygon after 3+ points and computes the true geodesic area (accounting for Earth curvature).</p>
          <p>Toggle satellite view to see plot boundaries, fences and roads. Undo to remove the last point, or Clear to start over.</p>
        </>}
        faqs={[
          { q: "How accurate is the area?", a: "Very accurate for plots up to a few square kilometres. We use the geodesic (spherical excess) formula on WGS84 — the same base used by Google Earth. Small errors come from your clicks, not the math." },
          { q: "Do I need an internet connection?", a: "Yes — the map tiles and address search fetch from OpenStreetMap. The area calculation itself runs entirely in your browser." },
          { q: "Can I export the polygon?", a: "Use Copy / CSV / PDF from the results panel — coordinates and area in every unit are exported. KML/GeoJSON export is on our roadmap." },
        ]}
        related={["land-converter","plot","far","stamp-duty"]}
      />
    </div>
  );
}
